import React, { useState } from 'react';
import { ExternalLink, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Skincare = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedProduct, setExpandedProduct] = useState(null);

  const productCategories = [
    {
      name: 'Anti-Age',
      description: 'Advanced anti-aging solutions backed by science',
      products: [
        {
          name: 'Chaperone Series',
          description: 'New generation anti-aging creams that actively discard worn-down cells, reawaken inactive cells, and repair aging damage.',
          keyIngredients: ['EGF (Epidermal Growth Factor)', 'Chaperone Peptide', '5-ALA Amino Acid'],
          benefits: ['Rejuvenates skin', 'Strengthens firmness', 'Smooths texture', 'Boosts collagen production']
        },
        {
          name: 'Crème Métamorphique',
          description: 'Patented Vitamin A anti-wrinkle cream - a must-have for those above 25 who wish to prevent aging signs.',
          keyIngredients: ['Two forms of Vitamin A'],
          benefits: ['Rebuilds collagen fibers', 'Reinforces skin', 'Reduces wrinkles', 'Prevents aging signs']
        },
        {
          name: 'Paradoxe Series',
          description: 'Super potent anti-age solutions with nearly medical properties.',
          keyIngredients: ['Chilean Procyanidin', 'Resveratrol', 'Vitamin A Esters'],
          benefits: ['Delays aging process', 'Repairs sun damage', 'Combats redness', 'Rebuilds healthy skin']
        },
        {
          name: 'Crème Symphonique',
          description: 'Ground-breaking products based on latest dermatological research.',
          keyIngredients: ['Six types of Vitamin A', 'Squalane nanotechnology', 'Multiple active ingredients'],
          benefits: ['Rejuvenates collagen network', 'Prevents aging', 'Rebuilds youth', 'Strengthens elasticity']
        }
      ]
    },
    {
      name: 'Superfruit Skin Enforcement',
      description: 'Nordic superfruits for youthful, glowing skin',
      products: [
        {
          name: 'Superfruit Moisture Serum',
          description: 'Unique water-based serum with Nordic superfruit extracts that prevent impurities and provide natural glow.',
          keyIngredients: ['Sea Buckthorn', 'Cloudberry', 'Lingonberry', 'Hyaluronic Acid', 'Niacinamide'],
          benefits: ['Prevents impurities', 'Even skin tone', 'Natural glow', 'Boosts moisture']
        },
        {
          name: 'Superfruit Day & Night Crème',
          description: 'Moisturizing creams that enhance youthfulness and reduce fine lines.',
          keyIngredients: ['Lingonberries', 'Green Tea', 'Apple Extracts'],
          benefits: ['Enhances youthfulness', 'Reduces fine lines', 'Protects cells', 'Balances moisture']
        }
      ]
    },
    {
      name: 'Cleansing',
      description: 'Gentle yet effective cleansing solutions',
      products: [
        {
          name: 'Cleansing Foam & Milk',
          description: 'Cleanses without irritating or drying, perfect for all skin types.',
          keyIngredients: ['Gentle cleansing agents'],
          benefits: ['Removes makeup', 'Clears pores', 'Non-irritating', 'Smooth finish']
        },
        {
          name: 'Bio-Dermabrasion Peeling Wipes',
          description: 'Gentle exfoliating wipes that eliminate dead skin cells.',
          keyIngredients: ['Bio-degradable dual-sided wipes'],
          benefits: ['Eliminates dead cells', 'Reduces fine lines', 'Evens complexion', 'Radiant skin']
        }
      ]
    },
    {
      name: 'Body Care',
      description: 'Complete body wellness and care',
      products: [
        {
          name: 'Enriched Moisturizing Body Lotion',
          description: 'Super potent moisturizing lotion, documented to increase moisture by 65% after 30 days.',
          keyIngredients: ['Squalane', 'Vitamin A'],
          benefits: ['Intense moisture', 'Soothes irritation', 'Treats eczema', 'Calms sunburn']
        },
        {
          name: 'Anti-Cellulite Gel',
          description: 'Clinically proven to reduce cellulite and thigh circumference.',
          keyIngredients: ['Active cellulite-reducing compounds'],
          benefits: ['Reduces cellulite', 'Firms skin', 'Improves texture', 'Visible results']
        }
      ]
    },
    {
      name: 'Masculinity',
      description: 'Professional grooming and skincare for men',
      products: [
        {
          name: 'Hair & Shower Gel',
          description: '2-in-1 shower gel for hair and body that cleanses and refreshes without causing dryness.',
          keyIngredients: ['Gentle cleansing agents'],
          benefits: ['Cleanses skin', 'Refreshes hair', 'Non-drying', 'Convenient']
        },
        {
          name: 'Purifying Foam Cleanser',
          description: 'One-step facial cleanser for all skin types providing fast, gentle cleansing.',
          keyIngredients: ['Mild cleansing foam'],
          benefits: ['Quick cleansing', 'Clears pores', 'Smooth skin', 'Radiant finish']
        },
        {
          name: 'Double Action Facial Scrub',
          description: 'Efficient exfoliating cream with cellulose beads that removes dead skin cells.',
          keyIngredients: ['Cellulose beads', 'Propolis', 'Rooibos'],
          benefits: ['Exfoliates effectively', 'Youthful glow', 'Loosens dead cells', 'Refreshing']
        },
        {
          name: 'Anti-Age Creme for Men',
          description: 'Powerful Vitamin A cream that prevents and repairs while reducing oiliness.',
          keyIngredients: ['Two types of Vitamin A', 'Squalane', 'Propolis', 'Rooibos'],
          benefits: ['Prevents aging', 'Repairs damage', 'Reduces oiliness', 'Fights impurities']
        },
        {
          name: 'Moisturizing Day Creme',
          description: 'Perfect moisturizer for masculine skin providing long-lasting hydration.',
          keyIngredients: ['Squalane', 'Hyaluronic Acid', 'Niacinamide'],
          benefits: ['Long-lasting moisture', 'Softens skin', 'Quick absorption', 'Daily protection']
        }
      ]
    }
  ];

  const studies = [
    {
      title: 'Vitamin A (Retinoids) in Skin Rejuvenation',
      summary: 'Clinical studies demonstrate that topical vitamin A derivatives effectively reduce fine lines, improve skin texture, and stimulate collagen production. Research shows measurable improvements in skin thickness and cellular turnover.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2699641/'
    },
    {
      title: 'Alpha Hydroxy Acids in Dermatology',
      summary: 'Scientific research confirms that AHAs improve skin texture, reduce fine lines, and promote cellular renewal. Studies show AHAs enhance skin barrier function and increase collagen synthesis.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3047947/'
    },
    {
      title: 'Vitamin C in Dermatology',
      summary: 'Research demonstrates vitamin C\'s role in collagen synthesis, antioxidant protection, and skin brightening. Clinical trials show it can reduce hyperpigmentation and protect against UV damage.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5605218/'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      await axios.post(`${API}/inquiries`, {
        section: 'skincare',
        ...formData
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredCategories = selectedCategory === 'all' 
    ? productCategories 
    : productCategories.filter(cat => cat.name.toLowerCase() === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(13, 40, 71, 0.8), rgba(13, 40, 71, 0.8)), url(https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/p9h3iuow_image.png)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <h1 className="text-5xl font-bold mb-4">Cosmeceutical Skincare</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Danish skincare excellence with scientifically proven results. Beauté Pacifique delivers near-medical efficacy.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/p9h3iuow_image.png" 
                alt="Beauté Pacifique Skincare"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0D2847] mb-6">About Beauté Pacifique</h2>
              <p className="text-gray-600 mb-4">
                Since 1997, Beauté Pacifique has been at the forefront of scientific skincare innovation. This Danish brand 
                has become internationally recognized for products that beauty experts and models swear by.
              </p>
              <p className="text-gray-600 mb-4">
                The groundbreaking Crème Métamorphique was the first skincare product in the world to document 
                significant skin rejuvenation through objective ultrasound scanning. Effect and documentation are the 
                two indispensable principles behind every Beauté Pacifique product.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Scientifically proven</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Medical-grade results</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Danish quality</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Documented results</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-[#0D2847]' : ''}
            >
              All Products
            </Button>
            {productCategories.map((cat) => (
              <Button
                key={cat.name}
                variant={selectedCategory === cat.name.toLowerCase() ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat.name.toLowerCase())}
                className={selectedCategory === cat.name.toLowerCase() ? 'bg-[#0D2847]' : ''}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-8 text-center">Product Collections</h2>
          
          <div className="space-y-12">
            {filteredCategories.map((category, catIndex) => (
              <div key={catIndex}>
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-[#0D2847] mb-2">{category.name}</h3>
                  <p className="text-gray-600">{category.description}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.products.map((product, prodIndex) => (
                    <Card key={prodIndex} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <CardTitle className="text-lg">{product.name}</CardTitle>
                        <CardDescription>{product.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <button
                          onClick={() => setExpandedProduct(expandedProduct === `${catIndex}-${prodIndex}` ? null : `${catIndex}-${prodIndex}`)}
                          className="w-full flex items-center justify-between text-[#0D2847] font-medium mb-2"
                        >
                          <span>View Details</span>
                          {expandedProduct === `${catIndex}-${prodIndex}` ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                        
                        {expandedProduct === `${catIndex}-${prodIndex}` && (
                          <div className="mt-4 space-y-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-1">Key Ingredients:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {product.keyIngredients.map((ingredient, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <Check className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={14} />
                                    {ingredient}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-1">Benefits:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {product.benefits.map((benefit, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <Check className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={14} />
                                    {benefit}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* M.O.R.E. Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-4 text-center">The M.O.R.E. Philosophy</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Beauté Pacifique's commitment to excellence through Medical Grade Skin Care, Organic Approach, Results, and Evidence
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Medical Grade</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">Near-medical efficacy with scientifically proven ingredients and formulations</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Organic Approach</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">Natural, vitamin-based therapy that works in harmony with your skin</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Results</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">Guaranteed visible results backed by clinical testing and real-world outcomes</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Evidence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">Clinically proven through double-blind studies and ultrasound documentation</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Scientific Studies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-4 text-center">Scientific Foundation</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Beauté Pacifique's effectiveness is rooted in proven dermatological science:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {studies.map((study, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{study.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4">{study.summary}</p>
                  <a 
                    href={study.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#0D2847] hover:underline text-sm font-medium flex items-center"
                  >
                    Read Full Study <ExternalLink size={14} className="ml-1" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Product Inquiry Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">Interested in Beauté Pacifique?</CardTitle>
              <CardDescription className="text-center">
                Contact us to learn more about our cosmeceutical skincare range and find the perfect products for your skin.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input 
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input 
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea 
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-[#0D2847]" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
                {submitStatus === 'success' && (
                  <p className="text-green-600 text-center">Thank you! We'll be in touch soon.</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-600 text-center">Something went wrong. Please try again.</p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Skincare;
